/* Hallmark · component: email OTP sign-in · genre: modern-minimal · theme: Seaa Active
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass · mobile: 320 px
 */
import { useState, type FormEvent } from 'react'
import { Check, LoaderCircle } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { authClient } from '../../lib/auth-client'
import { normalizeOtpInput } from '../../lib/auth-input'
import { useOtpResend } from './use-otp-resend'

interface EmailOtpFormProps {
  locale: 'ar' | 'en' | 'de' | 'nl'
  defaultEmail?: string
  lockedEmail?: boolean
  onVerified: (email: string) => void | Promise<void>
  submitLabel?: string
  showLocalCode?: boolean
}

const copy = {
  ar: {
    email: 'البريد الإلكتروني', emailHint: 'name@business.com', send: 'إرسال رمز الدخول',
    code: 'رمز الدخول', verify: 'التحقق والمتابعة', resend: 'استخدام بريد إلكتروني آخر',
    sent: 'إذا كان البريد مسجّلًا، فسيصلك رمز دخول من 6 أرقام.',
    generic: 'تعذّر التحقق من الرمز. راجعه أو اطلب رمزًا جديدًا.', local: 'رمز الاختبار المحلي: 123456',
    sendFailed: 'تعذّر إرسال الرمز. تحقق من اتصالك وأعد المحاولة.', sendAgain: 'إرسال رمز جديد', retry: 'إعادة المحاولة',
  },
  en: {
    email: 'Email address', emailHint: 'name@business.com', send: 'Send sign-in code',
    code: 'Sign-in code', verify: 'Verify and continue', resend: 'Use a different email',
    sent: 'If this email is registered, you will receive a six-digit sign-in code.',
    generic: 'We could not verify that code. Check it or request a new one.', local: 'Local test code: 123456',
    sendFailed: 'We could not send the code. Check your connection and try again.', sendAgain: 'Send a new code', retry: 'Try again',
  },
  de: {
    email: 'E-Mail-Adresse', emailHint: 'name@unternehmen.de', send: 'Anmeldecode senden',
    code: 'Anmeldecode', verify: 'Bestätigen und weiter', resend: 'Andere E-Mail verwenden',
    sent: 'Wenn diese E-Mail registriert ist, erhältst du einen sechsstelligen Anmeldecode.',
    generic: 'Der Code konnte nicht bestätigt werden. Prüfe ihn oder fordere einen neuen an.', local: 'Lokaler Testcode: 123456',
    sendFailed: 'Der Code konnte nicht gesendet werden. Prüfe deine Verbindung und versuche es erneut.', sendAgain: 'Neuen Code senden', retry: 'Erneut versuchen',
  },
  nl: {
    email: 'E-mailadres', emailHint: 'naam@bedrijf.nl', send: 'Inlogcode sturen',
    code: 'Inlogcode', verify: 'Verifiëren en doorgaan', resend: 'Ander e-mailadres gebruiken',
    sent: 'Als dit e-mailadres is geregistreerd, ontvang je een zescijferige inlogcode.',
    generic: 'We konden die code niet verifiëren. Controleer hem of vraag een nieuwe aan.', local: 'Lokale testcode: 123456',
    sendFailed: 'De code kon niet worden verzonden. Controleer je verbinding en probeer opnieuw.', sendAgain: 'Nieuwe code sturen', retry: 'Opnieuw proberen',
  },
} as const

export function EmailOtpForm({
  locale, defaultEmail = '', lockedEmail = false, onVerified, submitLabel, showLocalCode = false,
}: EmailOtpFormProps) {
  const text = copy[locale]
  const [email, setEmail] = useState(defaultEmail)
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code' | 'authenticated' | 'verified'>('email')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const resend = useOtpResend()

  const sendCode = async () => {
    if (pending || resend.seconds > 0) return
    setPending(true)
    setError('')
    const normalized = email.trim().toLowerCase()
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({ email: normalized, type: 'sign-in' })
      if (result.error) { setError(text.sendFailed); return }
      setEmail(normalized)
      setCode('')
      setStep('code')
      resend.start()
    } catch {
      setError(text.sendFailed)
    } finally {
      setPending(false)
    }
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (pending) return
    if (step === 'email') return sendCode()
    if (step === 'verified') return
    setPending(true)
    setError('')
    let authenticated = step === 'authenticated'
    try {
      if (step !== 'authenticated') {
        const result = await authClient.signIn.emailOtp({ email, otp: code })
        if (result.error) { setError(text.generic); return }
        // The single-use OTP is consumed. Retry workspace access without replaying it.
        setStep('authenticated')
        authenticated = true
      }
      await onVerified(email)
      setStep('verified')
    } catch (value) {
      setError(authenticated && value instanceof Error ? value.message : text.generic)
    } finally {
      setPending(false)
    }
  }

  return <form onSubmit={submit} className="auth-email-otp space-y-4" data-state={error ? 'error' : step === 'verified' ? 'success' : pending ? 'loading' : step}>
    <label className="block text-sm font-semibold">{text.email}
      <Input name="email" value={email} onChange={(event) => setEmail(event.target.value)} type="email" inputMode="email"
        autoComplete="email" dir="ltr" className="mt-2" placeholder={text.emailHint}
        disabled={step !== 'email' || pending || lockedEmail} required />
    </label>
    {step === 'code' && <>
      <p className="auth-email-otp__sent text-sm text-muted" role="status">{text.sent}</p>
      <label className="block text-sm font-semibold">{text.code}
        <Input name="verificationCode" value={code} onChange={(event) => setCode(normalizeOtpInput(event.target.value))}
          inputMode="numeric" autoComplete="one-time-code" dir="ltr" className="auth-email-otp__code mt-2 text-center"
          minLength={6} maxLength={6} disabled={pending} required autoFocus />
      </label>
      {showLocalCode && <p className="auth-email-otp__local">{text.local}</p>}
    </>}
    {error && <p role="alert" className="text-sm text-danger">{error}</p>}
    <Button type="submit" className="w-full" size="lg" disabled={pending || step === 'verified' || (step === 'email' && resend.seconds > 0)}>
      {pending ? <><LoaderCircle aria-hidden="true" className="auth-email-otp__spinner" size={18} />{step === 'email' ? text.send : text.verify}</>
        : step === 'verified' ? <><Check aria-hidden="true" size={18} />{submitLabel ?? text.verify}</>
          : step === 'authenticated' ? text.retry : submitLabel && step === 'code' ? submitLabel : step === 'email' ? `${text.send}${resend.seconds > 0 ? ` (${resend.seconds})` : ''}` : text.verify}
    </Button>
    {step === 'code' && <button type="button" disabled={pending || resend.seconds > 0}
      className="min-h-11 w-full text-sm font-semibold underline underline-offset-4 disabled:opacity-50" onClick={() => void sendCode()}>
      {text.sendAgain}{resend.seconds > 0 ? ` (${resend.seconds})` : ''}
    </button>}
    {(step === 'code' || step === 'authenticated') && !lockedEmail && <button type="button" disabled={pending} className="auth-email-otp__reset min-h-11 w-full text-sm font-semibold underline underline-offset-4"
      onClick={() => { setStep('email'); setCode(''); setError('') }}>{text.resend}</button>}
  </form>
}
